// ─── Audio ──────────────────────────────────────────────────────────────────
// Web equivalent of src/ui/useAudioLoop.ts + src/ui/useSfx.ts.
// - Background loop: one <audio loop>. We read `.currentTime * 1000` for the
//   beat position and feed it straight into src/game/audioSync.ts (pure, worklet-free).
// - Browsers block audio until a user gesture — everything starts on the game-start tap.
// - SFX: a tiny round-robin pool of <audio> clones so overlapping chain clears
//   don't cut each other off (same rationale as the native pooled useSfx).

import { MUSIC_URL } from './config';

type SfxName = 'place' | 'chain' | 'onbeat' | 'gameover';

const SFX_URLS: Record<SfxName, string> = {
  place: '/audio/sfx-place.mp3',
  chain: '/audio/sfx-chain.mp3',
  onbeat: '/audio/sfx-onbeat.mp3',
  gameover: '/audio/sfx-gameover.mp3',
};

export class AudioEngine {
  private music: HTMLAudioElement;
  private sfxPool: Record<SfxName, HTMLAudioElement[]> = {
    place: [],
    chain: [],
    onbeat: [],
    gameover: [],
  };
  private sfxIdx: Record<SfxName, number> = { place: 0, chain: 0, onbeat: 0, gameover: 0 };
  private started = false;
  muted = false;

  constructor() {
    this.music = new Audio(MUSIC_URL);
    this.music.loop = true;
    this.music.volume = 0.6;
    this.music.preload = 'auto';

    for (const name of Object.keys(SFX_URLS) as SfxName[]) {
      const count = name === 'gameover' ? 1 : 2;
      for (let i = 0; i < count; i++) {
        const a = new Audio(SFX_URLS[name]);
        a.preload = 'auto';
        a.volume = name === 'place' ? 0.35 : 0.5;
        this.sfxPool[name].push(a);
      }
    }
  }

  /** Call from the game-start tap handler. Safe to call repeatedly. */
  async start(): Promise<void> {
    if (this.started) {
      this.music.currentTime = 0;
      void this.music.play().catch(() => undefined);
      return;
    }
    this.started = true;
    try {
      this.music.currentTime = 0;
      await this.music.play();
    } catch {
      // Autoplay still blocked (rare after a gesture) — beat grid just stays quiet.
    }
  }

  stop(): void {
    this.music.pause();
    this.music.currentTime = 0;
  }

  /** Swap the background track (skin change). Resumes playback if it was playing. */
  setTrack(url: string): void {
    if (this.music.src.endsWith(url)) return;
    const wasPlaying = !this.music.paused;
    this.music.pause();
    this.music.src = url;
    this.music.currentTime = 0;
    if (wasPlaying) void this.music.play().catch(() => undefined);
  }

  pause(): void {
    this.music.pause();
  }

  resume(): void {
    void this.music.play().catch(() => undefined);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.music.muted = m;
  }

  /**
   * Playback position in ms — feeds src/game/audioSync.ts. Returns -1 while the
   * music is not actually playing, so callers don't treat "no audio yet" as a
   * permanent on-beat (isOnBeat(0) is true).
   */
  positionMs(): number {
    if (this.music.paused || this.music.currentTime === 0) return -1;
    return this.music.currentTime * 1000;
  }

  playSfx(name: SfxName): void {
    if (this.muted) return;
    const pool = this.sfxPool[name];
    const a = pool[this.sfxIdx[name]];
    this.sfxIdx[name] = (this.sfxIdx[name] + 1) % pool.length;
    try {
      a.currentTime = 0;
      void a.play().catch(() => undefined);
    } catch {
      /* ignore */
    }
  }
}
